import { useState } from "react";
import { Layers, Clock, Loader2, Check, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/hooks/useT";
import { trackEvent } from "@/lib/posthog";
import { toast } from "sonner";

/**
 * MicroChunker — renders a "Chunk It" button that turns a block of AI-generated
 * content into 3–5 atomic 3-5 minute tasks the learner can actually do.
 * Powers the Cognitive Bridge Care Suite.
 *
 * Props:
 *   - getText(): () => string  — called when the user clicks; returns the text to chunk
 *   - context?: string         — optional lesson title / step label for context
 *   - testIdPrefix?: string    — disambiguate when multiple chunkers live on a page
 */
export default function MicroChunker({ getText, context, testIdPrefix = "chunker" }) {
  const { lang } = useT();
  const [busy, setBusy] = useState(false);
  const [tasks, setTasks] = useState(null);
  const [done, setDone] = useState(() => new Set());

  const run = async () => {
    const text = (typeof getText === "function" ? getText() : "") || "";
    if (!text.trim()) {
      toast.error("Nothing to chunk yet — wait for the lesson to finish.");
      return;
    }
    setBusy(true);
    try {
      trackEvent("micro_chunk_requested", {
        language: lang,
        char_count: text.length,
        context: context || null,
      });
      const { data } = await api.post("/ai/chunk", {
        text,
        language: lang,
        context: context || null,
      });
      setTasks(data?.tasks || []);
      setDone(new Set());
      trackEvent("micro_chunk_rendered", {
        language: lang,
        task_count: (data?.tasks || []).length,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chunker unavailable — try again.");
    } finally {
      setBusy(false);
    }
  };

  const toggleDone = (i) => {
    setDone((prev) => {
      const n = new Set(prev);
      if (n.has(i)) {
        n.delete(i);
      } else {
        n.add(i);
        trackEvent("micro_chunk_task_completed", { index: i });
        // Ping the server streak counter + notify the header badge. The
        // backend is idempotent per UTC day so multiple calls are cheap.
        api.post("/streak/chunk-ping").then(() => {
          try { window.dispatchEvent(new Event("oak:streak-ping")); } catch {}
        }).catch(() => {});
      }
      return n;
    });
  };

  return (
    <div className="mt-4" data-testid={`${testIdPrefix}-root`}>
      {!tasks && (
        <button
          onClick={run}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-display font-semibold border border-[#00FF9D]/40 text-[#00FF9D] bg-[#00FF9D]/[0.05] hover:bg-[#00FF9D]/[0.12] transition disabled:opacity-60"
          data-testid={`${testIdPrefix}-button`}
          title="Break this into 3–5 minute atomic tasks"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Layers size={12} />}
          {busy ? "Chunking…" : "Chunk It · 3–5 min tasks"}
        </button>
      )}

      {tasks && (
        <div
          className="rounded-2xl border border-[#00FF9D]/25 bg-[#00FF9D]/[0.04] p-4 md:p-5 space-y-3"
          data-testid={`${testIdPrefix}-panel`}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Layers size={14} className="text-[#00FF9D]" />
              <h4 className="font-display text-sm tracking-tight text-white">
                Atomic Tasks · {tasks.length}
              </h4>
              <span className="text-[10px] text-white/40 font-mono-oak">
                total ≈ {tasks.reduce((s, t) => s + (t.minutes || 0), 0)} min
              </span>
            </div>
            <button
              onClick={() => { setTasks(null); setDone(new Set()); }}
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] text-white/55 hover:text-white border border-white/10 hover:border-white/25 transition"
              data-testid={`${testIdPrefix}-reset`}
            >
              <RotateCcw size={11} /> Reset
            </button>
          </div>

          <ul className="space-y-2.5">
            {tasks.map((t, i) => {
              const isDone = done.has(i);
              return (
                <li
                  key={i}
                  className={`rounded-xl border p-3 transition ${
                    isDone
                      ? "border-[#00FF9D]/40 bg-[#00FF9D]/[0.08]"
                      : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
                  }`}
                  data-testid={`${testIdPrefix}-task-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => toggleDone(i)}
                      className={`mt-0.5 h-5 w-5 shrink-0 rounded-md border grid place-items-center transition ${
                        isDone
                          ? "bg-[#00FF9D]/80 border-[#00FF9D] text-black"
                          : "border-white/25 text-transparent hover:border-white/50"
                      }`}
                      aria-pressed={isDone}
                      aria-label={`Mark task ${i + 1} done`}
                      data-testid={`${testIdPrefix}-check-${i}`}
                    >
                      <Check size={12} strokeWidth={3} />
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className={`flex items-center gap-2 flex-wrap ${isDone ? "line-through text-white/50" : ""}`}>
                        <span className="font-display font-semibold text-[14px] text-white">
                          {i + 1}. {t.title}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] border border-[#8aa8ff]/30 text-[#8aa8ff] bg-[#8aa8ff]/10 font-mono-oak">
                          <Clock size={10} /> {t.minutes} min
                        </span>
                      </div>
                      <div className={`text-[13px] leading-relaxed text-white/75 mt-1 ${isDone ? "line-through text-white/40" : ""}`}>
                        {t.action}
                      </div>
                      {t.why && (
                        <div className="text-[11px] text-white/45 mt-1.5 italic">
                          Why: {t.why}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="flex items-center justify-between text-[11px] text-white/45 font-mono-oak pt-1">
            <span>Completed {done.size} / {tasks.length}</span>
            <span>Natively generated via Oak AI Campus</span>
          </div>
        </div>
      )}
    </div>
  );
}
